import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemConfirm,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAdminTeamsEmitter } from "@/hooks/useAdminTeamsEvents";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { AdminTeamDTO, TeamStatus } from "@shaarp/common";
import { ChevronDownIcon, ExternalLinkIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/button";
import { useToast, useToastError } from "../ui/use-toast";
import { DialogAdminTeamAdvisor } from "./dialog-admin-team-advisor";
import { DialogAdminTeamReferral } from "./dialog-admin-team-referral";
import { DialogAdminTeamTrial } from "./dialog-admin-team-trial";

export default function DataTableCellAdminsActions({
  team,
}: {
  team: AdminTeamDTO;
}) {
  const toastError = useToastError();
  const { toast } = useToast();
  const api = useApi();
  const { t } = useTranslation();
  const { refreshTeams } = useAdminTeamsEmitter();

  const [openTeamTrialModal, setOpenTeamTrialModal] = useState(false);
  const [openTeamReferralModal, setOpenTeamReferralModal] = useState(false);
  const [openTeamAdvisorModal, setOpenTeamAdvisorModal] = useState(false);

  const doEnableOrDisableTeam = async (status: TeamStatus) => {
    try {
      team.status = status;
      await api.post(`/admin/team/${team.id}`, {
        status,
      });
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.toast.teamModified"),
        description:
          status === "enabled"
            ? t("admin.dataTableAdminTeams.toast.teamEnabled")
            : t("admin.dataTableAdminTeams.toast.teamDisabled"),
      });
      refreshTeams();
    } catch (e) {
      team.status = status;
      toastError();
    }
  };

  const doArchiveTeam = async () => {
    try {
      await api.post(`/admin/team/${team.id}`, { status: "archived" });
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.toast.teamModified"),
        description: t("admin.dataTableAdminTeams.toast.teamArchived"),
      });
      refreshTeams();
    } catch (e) {
      toastError();
    }
  };

  const doUnarchiveTeam = async () => {
    try {
      await api.post(`/admin/team/${team.id}`, { status: "enabled" });
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.toast.teamModified"),
        description: t("admin.dataTableAdminTeams.toast.teamUnarchived"),
      });
      refreshTeams();
    } catch (e) {
      toastError();
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            {t("admin.dataTableAdminTeams.actions.actions")}
            <ChevronDownIcon className="ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>
            {t("admin.dataTableAdminTeams.actions.team")}
          </DropdownMenuLabel>

          {team.status !== "archived" && (
            <>
              <DropdownMenuItemConfirm
                text={
                  team.status === "enabled"
                    ? t("admin.dataTableAdminTeams.actions.confirmDisable")
                    : t("admin.dataTableAdminTeams.actions.confirmEnable")
                }
                onConfirm={() =>
                  doEnableOrDisableTeam(
                    team.status === "enabled" ? "disabled" : "enabled"
                  )
                }
                className={team.status === "enabled" ? "text-destructive" : ""}
              >
                {team.status === "enabled"
                  ? t("admin.dataTableAdminTeams.actions.disableTeam")
                  : t("admin.dataTableAdminTeams.actions.enableTeam")}
              </DropdownMenuItemConfirm>
              <DropdownMenuItemConfirm
                text={t("admin.dataTableAdminTeams.actions.confirmArchive")}
                onConfirm={() => doArchiveTeam()}
                className={"text-destructive"}
              >
                {t("admin.dataTableAdminTeams.actions.archiveTeam")}
              </DropdownMenuItemConfirm>
            </>
          )}

          {team.status === "archived" && (
            <DropdownMenuItemConfirm
              text={t("admin.dataTableAdminTeams.actions.confirmUnarchive")}
              onConfirm={() => doUnarchiveTeam()}
            >
              {t("admin.dataTableAdminTeams.actions.unarchiveTeam")}
            </DropdownMenuItemConfirm>
          )}

          <DropdownMenuItem onClick={() => setOpenTeamTrialModal(true)}>
            {t("admin.dataTableAdminTeams.actions.modifyTrialPeriod")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setOpenTeamReferralModal(true)}>
            {t("admin.dataTableAdminTeams.actions.modifyReferral")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setOpenTeamAdvisorModal(true)}>
            {t("admin.dataTableAdminTeams.actions.modifyAdvisor")}
          </DropdownMenuItem>
          {import.meta.env.VITE_POSTHOG_PROJECT_ID && (
            <DropdownMenuItem
              onClick={() =>
                window.open(
                  `https://eu.posthog.com/project/${import.meta.env.VITE_POSTHOG_PROJECT_ID}/groups/0/${team.id}`,
                  "_blank"
                )
              }
            >
              PostHog
              <ExternalLinkIcon className="ml-auto h-4 w-4" />
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {openTeamTrialModal && (
        <DialogAdminTeamTrial
          team={team}
          open={openTeamTrialModal}
          onClose={() => setOpenTeamTrialModal(false)}
        />
      )}

      {openTeamReferralModal && (
        <DialogAdminTeamReferral
          team={team}
          open={openTeamReferralModal}
          onClose={() => setOpenTeamReferralModal(false)}
        />
      )}

      {openTeamAdvisorModal && (
        <DialogAdminTeamAdvisor
          team={team}
          open={openTeamAdvisorModal}
          onClose={() => setOpenTeamAdvisorModal(false)}
        />
      )}
    </>
  );
}
